import { useCallback, useMemo } from "react"
import { useSelector } from "react-redux"

import type {
  DeviceAlertSettingWState,
  GroupAlertSettingWState,
  PowerlineAlertSettingWState,
  TowerAlertSettingWState,
} from "types/alerts.types"

import { useOrgAlertSettingsStatesQuery } from "features/api"
import { selectCurrentOrgId } from "features/store/orgSlice"
import {
  formatSettingsByPowerlineId,
  formatSettingsByDeviceId,
  formatSettingsByGroupId,
  formatStateBySettingId,
  formatSettingsByTowerId,
  formatResOrgAlertSettingsWStates,
} from "helpers/formatters/alertSettingsFormatters"
import { ALERT_TIME } from "helpers/utils/constants"

const useAlertSettingsWStates = () => {
  const orgId = useSelector(selectCurrentOrgId)
  const {
    currentData: resOrgAlertSettingsWStates,
    isLoading,
    isFetching,
    isError,
    fulfilledTimeStamp,
    refetch,
  } = useOrgAlertSettingsStatesQuery(
    {
      org_id: orgId as number,
    },
    {
      pollingInterval: ALERT_TIME,
      skip: !orgId,
    },
  )

  const orgAlertSettingsWStates = useMemo(
    () => formatResOrgAlertSettingsWStates(resOrgAlertSettingsWStates),
    [resOrgAlertSettingsWStates],
  )

  const {
    alertSettingsByDeviceId,
    alertSettingsByGroupId,
    alertSettingsByPowerlineId,
    alertSettingsByTowerId,
  } = useMemo(() => {
    const alertSettingsByDeviceId = formatSettingsByDeviceId(orgAlertSettingsWStates)
    const alertSettingsByGroupId = formatSettingsByGroupId(orgAlertSettingsWStates)
    const alertSettingsByPowerlineId = formatSettingsByPowerlineId(
      orgAlertSettingsWStates,
    )
    const alertSettingsByTowerId = formatSettingsByTowerId(orgAlertSettingsWStates)
    return {
      alertSettingsByDeviceId,
      alertSettingsByGroupId,
      alertSettingsByPowerlineId,
      alertSettingsByTowerId,
    }
  }, [orgAlertSettingsWStates])

  const statesBySettingId = useMemo(
    () => formatStateBySettingId(orgAlertSettingsWStates),
    [orgAlertSettingsWStates],
  )

  const orgDevicesAlertSettingsWStates = useMemo(
    () =>
      (orgAlertSettingsWStates || []).filter(
        (settingState) => settingState.entity === "device",
      ) as DeviceAlertSettingWState[],
    [orgAlertSettingsWStates],
  )

  const getDeviceAlertSettingsWStatesByEntityId = useCallback(
    (entityId: number) =>
      orgDevicesAlertSettingsWStates.filter(
        (settingState) => settingState.entity_id === entityId,
      ),
    [orgDevicesAlertSettingsWStates],
  )

  const orgPowerlinesAlertSettingsWStates = useMemo(
    () =>
      (orgAlertSettingsWStates || []).filter(
        (settingState) => settingState.entity === "powerline",
      ) as PowerlineAlertSettingWState[],
    [orgAlertSettingsWStates],
  )

  const getPowerlineAlertSettingsWStatesByEntityId = useCallback(
    (entityId: number) =>
      orgPowerlinesAlertSettingsWStates.filter(
        (settingState) => settingState.entity_id === entityId,
      ),
    [orgPowerlinesAlertSettingsWStates],
  )

  const orgGroupsAlertSettingsWStates = useMemo(
    () =>
      (orgAlertSettingsWStates || []).filter(
        (settingState) => settingState.entity === "group",
      ) as GroupAlertSettingWState[],
    [orgAlertSettingsWStates],
  )

  const getGroupAlertSettingsWStatesByEntityId = useCallback(
    (entityId: number) =>
      orgGroupsAlertSettingsWStates.filter(
        (settingState) => settingState.entity_id === entityId,
      ),
    [orgGroupsAlertSettingsWStates],
  )

  const orgTowersAlertSettingsWStates = useMemo(
    () =>
      (orgAlertSettingsWStates || []).filter(
        (settingState) => settingState.entity === "tower",
      ) as TowerAlertSettingWState[],
    [orgAlertSettingsWStates],
  )

  const getTowersAlertSettingsWStatesByEntityId = useCallback(
    (entityId: number) =>
      orgTowersAlertSettingsWStates.filter(
        (settingState) => settingState.entity_id === entityId,
      ),
    [orgTowersAlertSettingsWStates],
  )

  return {
    orgAlertSettingsWStates,
    orgDevicesAlertSettingsWStates,
    getDeviceAlertSettingsWStatesByEntityId,
    orgPowerlinesAlertSettingsWStates,
    getPowerlineAlertSettingsWStatesByEntityId,
    orgGroupsAlertSettingsWStates,
    getGroupAlertSettingsWStatesByEntityId,
    orgTowersAlertSettingsWStates,
    getTowersAlertSettingsWStatesByEntityId,
    statesBySettingId,
    alertSettingsByDeviceId,
    alertSettingsByGroupId,
    alertSettingsByPowerlineId,
    alertSettingsByTowerId,
    isFetching,
    isLoading,
    isError,
    fulfilledTimeStamp,
    refetch,
  }
}

export default useAlertSettingsWStates
